/**
 * Conversation list (with search) → tapping a card opens SessionScreen.
 */
import { useEffect, useMemo, useState } from "react";
import * as api from "../api";
import { Field, IconBtn } from "../ui";
import { Icon } from "../icons";
import type { Go } from "../App";

export function History({ go }: { go: Go }) {
  const [sessions, setSessions] = useState<api.SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.listSessions()
      .then((s) => setSessions(s))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return sessions;
    const lc = q.toLowerCase();
    return sessions.filter((s) => {
      const d = new Date(s.started_at).toLocaleDateString().toLowerCase();
      return d.includes(lc) || String(s.id).includes(lc);
    });
  }, [q, sessions]);

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "32px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 18 }}>
        <h1 className="display" style={{ fontSize: 28, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.6 }}>
          Conversations
        </h1>
        <div className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{sessions.length} total</div>
      </div>
      <div style={{ marginBottom: 18, maxWidth: 380 }}>
        <Field icon="search" value={q} onChange={setQ} placeholder="Search by date or id" />
      </div>
      {loading ? (
        <div style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "var(--ink-3)" }}>{sessions.length === 0 ? "No conversations yet." : "No matches."}</div>
      ) : (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {filtered.map((s) => <HistoryCard key={s.id} s={s} onOpen={() => go("session", { sessionId: s.id })} />)}
        </div>
      )}
    </div>
  );
}

function HistoryCard({ s, onOpen }: { s: api.SessionListItem; onOpen: () => void }) {
  const start = new Date(s.started_at);
  const end = s.ended_at ? new Date(s.ended_at) : null;
  const date = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const time = start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const dur = end ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 1000)) : null;
  return (
    <button onClick={onOpen} style={{
      textAlign: "left", cursor: "pointer", border: "1px solid var(--border)",
      background: "var(--surface)", borderRadius: "var(--r-lg)", padding: "18px 20px",
      display: "flex", flexDirection: "column", gap: 10,
      transition: "transform 0.12s var(--ease), box-shadow 0.15s",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="display" style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>{date}</div>
        <IconBtn name="chevronRight" onClick={onOpen} size={28} ic={16} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--ink-3)", fontSize: 13 }}>
        <Icon name="clock" size={14} /> {time}{dur != null ? ` · ${fmtDur(dur)}` : ""}
      </div>
      <div className="mono" style={{ fontSize: 11.5, color: "var(--ink-faint)" }}>
        {s.utterance_count} utterances · #{s.id}
      </div>
    </button>
  );
}

function fmtDur(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60), s = secs % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
